import { useMemo } from "react";
import { useAuthenticated } from "../auth";
import { portalServer } from "../config";
import {
  type SyncModule,
  useSyncData,
  useSyncModuleRegistration,
} from "../sync";
import type { KartenObjekt, KartenObjektDatei, KartenObjektLink } from "../types";
import { limiter } from "./demo";
import { usePortalApi } from "./portal-api";
import { daily } from "./provider";
import { precacheResources } from "./resource";

const syncModuleId = "kartendetailobjekte";
const precacheSyncModuleId = "kartendetailobjekte:precache";

function useKartenDetailObjekteSyncModule(): SyncModule<
  ReadonlyArray<KartenObjekt>
> {
  const { portalKey, demo } = useAuthenticated();
  const { useNewApi, baseUrl } = usePortalApi();

  return useMemo(
    () => ({
      id: syncModuleId,
      i18nKey: `karte:tasks.${syncModuleId}`,
      defaultOptions: {
        schedule: { type: "periodic", interval: daily },
      },
      fetchData: useNewApi && baseUrl
        ? async () =>
            limiter<KartenObjekt>(demo, 100)(
              await fetchKartenDetailObjekteNew(portalKey, baseUrl),
            )
        : async () => {
            const response = await fetch(
              `${portalServer}/get-detailobjects.php?ORG_ACCESSKEY=${encodeURIComponent(
                portalKey,
              )}`,
            );

            if (!response.ok) {
              throw new Error(
                `Error fetching data: ${response.status} ${response.statusText}`,
              );
            }

            const data = await response.json();
            const limited = limiter<KartenObjekt>(demo, 100);
            return limited(data.DETAILOBJEKT_LIST ?? []).map(
              parseKartenDetailobjektLegacy,
            );
          },
    }),
    [demo, portalKey, useNewApi, baseUrl],
  );
}

async function fetchKartenDetailObjekteNew(
  portalKey: string,
  baseUrl: string,
): Promise<ReadonlyArray<KartenObjekt>> {
  const response = await fetch(`${baseUrl}/api/device/sync/pois`, {
    headers: { Authorization: `Bearer ${portalKey}` },
  });

  if (!response.ok) {
    throw new Error(
      `Error fetching data: ${response.status} ${response.statusText}`,
    );
  }

  const data = await response.json();
  return (data.pois ?? []).map((entry: any) =>
    parseKartenDetailobjektNew(entry, portalKey, baseUrl),
  );
}

function parseKartenDetailobjektNew(
  entry: any,
  portalKey: string,
  baseUrl: string,
): KartenObjekt {
  const beschreibungParts: string[] = [];
  if (entry.access) beschreibungParts.push(entry.access);
  if (entry.remarks) beschreibungParts.push(entry.remarks);

  return {
    id: entry.id,
    typ: parseIconNew(entry.icon),
    icon: parsePoiCustomIconNew(entry.customIcon),
    istDetailobjekt: true,
    latitude: entry.latitude,
    longitude: entry.longitude,
    name: entry.name,
    adresse: entry.address || undefined,
    beschreibung: beschreibungParts.length > 0 ? beschreibungParts.join(" · ") : undefined,
    links: parsePoiLinksNew(entry),
    polygon: entry.area ? parsePolygonGeoJson(entry.area) : undefined,
    dateien: parsePoiDateienNew(entry.images, portalKey, baseUrl),
  };
}

function parsePoiLinksNew(entry: any): ReadonlyArray<KartenObjektLink> | undefined {
  const links: KartenObjektLink[] = [];
  if (entry.webLink) links.push({ id: "weblink", url: entry.webLink });
  if (entry.documentId)
    links.push({ id: "document", url: `file:${entry.documentId}` });
  if (entry.folderId)
    links.push({ id: "folder", url: `folder:${entry.folderId}` });
  return links.length > 0 ? links : undefined;
}

function parsePoiDateienNew(
  images: any[],
  portalKey: string,
  baseUrl: string,
): ReadonlyArray<KartenObjektDatei> | undefined {
  if (!images?.length) return undefined;

  const dateien = images
    .toSorted((a: any, b: any) => (a.order ?? 0) - (b.order ?? 0))
    .map((image: any) => ({
      id: image.id,
      name: image.originalName || image.filename,
      mimeType: image.mimeType,
      url: `${baseUrl}/api/device/sync/pois/images/${image.id}/download`,
      fetchOptions: { headers: { Authorization: `Bearer ${portalKey}` } },
    }));

  return dateien.length > 0 ? dateien : undefined;
}

// New API: "icon_allgemein_artikel.png" → "allgemein_artikel"
// Icon keys in @rescuetablet/map-icons are lowercase snake_case, but the API
// may return mixed case (e.g. "FW_Sammelpunkt"), so normalize to match.
function parseIconNew(icon?: string): string {
  if (!icon) return "objekt";
  return icon
    .replace(/^icon_/, "")
    .replace(/\.[^.]+$/, "")
    .toLowerCase();
}

// New API: entries can carry an arbitrary custom icon independent of `icon`
// (e.g. "icon_we_h_we.png"), already matching a @rescuetablet/map-icons key
// once the extension is stripped. Falls back to the icon derived from
// `icon`/`typ` above when absent — same convention as the water supply points.
function parsePoiCustomIconNew(customIcon?: string | null): string | undefined {
  if (!customIcon) return undefined;
  return customIcon.replace(/\.[^.]+$/, "");
}

// GeoJSON Polygon coordinates are [lng, lat] — swap to [lat, lng] for Leaflet
function parsePolygonGeoJson(area: any): Array<[number, number]> {
  const ring: number[][] = area?.coordinates?.[0] ?? [];
  return ring.map((coord) => [coord[1], coord[0]] as [number, number]);
}

export function useKartenDetailObjekteSync() {
  const module = useKartenDetailObjekteSyncModule();
  useSyncModuleRegistration(module);
}

function useKartenDetailObjektePrecacheSyncModule(): SyncModule<void> {
  return useMemo(
    () => ({
      id: precacheSyncModuleId,
      i18nKey: `karte:tasks.${syncModuleId}.precache`,
      defaultOptions: {
        schedule: { type: "daily", hour: 3, minute: 0 },
        requiredNetworkStatus: ["wifi"],
      },
      dependsOn: [syncModuleId],
      fetchData: async ({ dataProvider, observer }) => {
        const objekte =
          dataProvider.getModuleData<ReadonlyArray<KartenObjekt>>(
            syncModuleId,
          ) ?? [];
        const dateien = objekte.flatMap((o) => o.dateien ?? []);
        if (dateien.length === 0) return;

        await precacheResources(
          dateien.map((d) => d.url),
          { requestOptions: dateien[0].fetchOptions },
          observer,
        );
      },
    }),
    [],
  );
}

export function useKartenDetailObjektePrecacheSync() {
  const module = useKartenDetailObjektePrecacheSyncModule();
  useSyncModuleRegistration(module);
}

export function useKartenDetailObjekte(): ReadonlyArray<KartenObjekt> {
  return useSyncData(syncModuleId) ?? [];
}

function parseKartenDetailobjektLegacy(entry: any): KartenObjekt {
  return {
    id: entry.DETAIL_ID,
    // Legacy: "map_icon_allgemein_artikel" → "allgemein_artikel"
    typ: entry.DETAIL_ICON.replace(/^map_icon_/, "").toLowerCase(),
    istDetailobjekt: true,
    latitude: parseFloat(entry.DETAIL_GEO_LAT),
    longitude: parseFloat(entry.DETAIL_GEO_LNG),
    name: entry.DETAIL_NAME,
    beschreibung: entry.DETAIL_DESCRIPTION || undefined,
    color: entry.DETAIL_COLOR || undefined,
    polygon: entry.DETAIL_BLOCK ? parsePolygonLegacy(entry.DETAIL_BLOCK) : undefined,
  };
}

function parsePolygonLegacy(polygon: string): Array<[number, number]> {
  const nodes = polygon.split(/\s*;\s*/);
  return nodes.map(
    (node) => node.split(/\s*,\s*/).map(parseFloat) as [number, number],
  );
}
